import classNameGenerator from './classNameGenerator'
import moment from 'moment'

// Texto de transparência sobre o prêmio extra de uma subcampanha.
// 1:1 -> não menciona proporção (todo bilhete já concorre).
// X:1 -> deixa claro que a cada X bilhetes o cliente ganha uma chance.
// O segundo argumento é o active_boost da sub (aceita também só o número do
// ratio boostado, formato antigo). Dois rendimentos possíveis:
//   boosted_ratio -> proporção reduzida, cita a normal entre parênteses;
//   multiplier    -> proporção menor que 1, cada bilhete vale N chances.
// Subcampanhas somente com prêmios instantâneos não têm prize_description/draw_time.
export function subcampaignAwarenessText(sub, boost = null) {
  if (!sub) return '';
  const isObject = boost !== null && typeof boost === 'object';
  const boostedRatio = isObject ? boost.boosted_ratio : boost;
  const multiplier = isObject ? boost.multiplier : null;
  let text;
  if (sub.prize_description) {
    text = `Comprando agora você também concorre a ${sub.prize_description}`;
    if (sub.draw_time) {
      text += `, sorteio ${moment(sub.draw_time).format('DD/MM/YYYY [às] HH:mm')}`;
    }
    text += '.';
  } else {
    // Instant-only subcampaign: no main prize, awards instant prizes at purchase time.
    text = `Comprando agora você também concorre a prêmios instantâneos`;
    if (sub.instant_prize_tickets && sub.instant_prize_tickets > 0) {
      text += ` (${sub.instant_prize_tickets} prêmio${sub.instant_prize_tickets > 1 ? 's' : ''} disponíve${sub.instant_prize_tickets > 1 ? 'is' : 'l'})`;
    }
    text += '.';
  }
  const boosted = boostedRatio && sub.ratio > 1 && boostedRatio < sub.ratio;
  if (multiplier > 1) {
    const before = sub.ratio > 1
      ? `antes uma chance a cada ${sub.ratio} bilhetes`
      : 'antes uma chance por bilhete';
    text += ` Agora cada bilhete comprado vale ${multiplier} chances de concorrer (${before}).`;
  } else if (boosted && boostedRatio === 1) {
    text += ` Agora todo bilhete vale uma chance de concorrer (antes a cada ${sub.ratio} bilhetes).`;
  } else if (boosted) {
    text += ` A cada ${boostedRatio} bilhetes (antes ${sub.ratio}), você ganha uma chance de concorrer.`;
  } else if (sub.ratio > 1) {
    text += ` A cada ${sub.ratio} bilhetes, você ganha uma chance de concorrer.`;
  }
  return text;
}

// Mínimo de títulos que o visitante pode comprar. special_offer_min_amount só
// vem no payload quando a session tem uma oferta ativa para aquela campanha
// (links ?oferta / ?oferta_minima) — fora disso cai no mínimo normal.
// raised_min_amount é o caminho inverso (link ?minimo=): mínimo ACIMA do
// normal. O backend nunca manda os dois juntos — com os dois links vale a oferta.
export function minPurchasableTickets(campaign) {
  return campaign?.special_offer_min_amount
    || campaign?.raised_min_amount
    || campaign?.min_amount
    || 1;
}

// Quantidade que a tela abre pré-selecionada. Com oferta ativa abre no mínimo
// da oferta: initial_ticket_selection é sempre >= min_amount normal (validado
// no model), então usá-lo esconderia o benefício do link. Com mínimo elevado a
// pré-seleção sobe até ele, senão a tela abriria num pedido que o backend recusa.
export function initialTicketSelection(campaign) {
  if (campaign?.special_offer_min_amount) return campaign.special_offer_min_amount;

  const initial = campaign?.initial_ticket_selection
    || campaign?.min_amount
    || 1;
  return Math.max(initial, campaign?.raised_min_amount || 0);
}

// Proporção do banner grande da campanha: a mesma dos arquivos cadastrados
// (principal 3,6:1 — ex. 1920x533; mobile 2:1 — ex. 1200x600), para a arte
// aparecer inteira em qualquer largura de tela. A página /lp usa os mesmos
// arquivos, então uma arte só serve aos dois layouts.
export function campaignBannerAspectRatio(isMobile) {
  return isMobile ? '2 / 1' : '3.6 / 1';
}

// Com a página de venda como interface principal (global_schema.lp_campaign_ids
// vem do servidor), a página de uma campanha à venda não é mais do SPA: quem
// chega nela navegando por dentro do React precisa de um carregamento completo
// para o servidor entregar a página de venda.
const LANDING_RELOAD_GUARD_MS = 30000;

// Id da campanha em /site/campanhas/:id (com ou sem o influencer na frente).
export function landingCampaignId(pathname) {
  const match = /^(?:\/[^/]+)?\/site\/campanhas\/(\d+)\/?$/.exec(pathname || '');
  return match ? Number(match[1]) : null;
}

// Decide a recarga e a registra. Não repete para o mesmo caminho dentro da
// janela de guarda: se o servidor devolver o SPA de novo (campanha acabou de
// sair de venda), a página fica no React em vez de recarregar em loop.
export function shouldReloadForLanding(pathname, campaignIds, storage, now = Date.now()) {
  const id = landingCampaignId(pathname);
  if (id === null || !(campaignIds || []).includes(id)) return false;

  const key = `lp_reload:${pathname}`;
  try {
    const last = Number(storage.getItem(key));
    if (last && now - last < LANDING_RELOAD_GUARD_MS) return false;
    storage.setItem(key, String(now));
  } catch (error) {
    return false; // sem storage não há como evitar o loop: fica no SPA
  }
  return true;
}

export function classNames(styles) {
  function myClassNames(names) {
    let classes = names.split(' ').reduce((str, name) => {
      return str + `${styles[name]} `
    }, '')
    return classes.trim();
  }

  return myClassNames;
}

// Função pra formatar um número como R$, recebendo um número ou string, e retornando uma string
export function numberAsCurrency(number, show_currency = true) {
  if (number == null) {
    return '-';
  }
  return `${show_currency ? 'R$ ' : ''}${parseFloat(number).toFixed(2).replace('.', ',').replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.')}`;
}

export async function copyToClipboard(textToCopy) {
  // Navigator clipboard api needs a secure context (https)
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(textToCopy);
  } else {
    const textArea = document.createElement("textarea");
    textArea.value = textToCopy;
    // Move textarea out of the viewport so it's not visible
    textArea.style.position = "absolute";
    textArea.style.left = "-999999px";    
    document.body.prepend(textArea);
    textArea.select();
    try {
        document.execCommand('copy');
    } catch (error) {
        console.error(error);
    } finally {
        textArea.remove();
    }
  }
}

export {
  pushDataLayer,
  gtmUserData,
  buildUserPayload,
  buildEventId,
  trackEvent
} from './dataLayer';

export {
  enqueueEvent,
  flushPending,
  wasSent,
  markSent
} from './trackingStore';